/**
 * DpaGateModal — bloqueio LGPD bloqueante (Launch-08, 08/06/2026)
 *
 * Renderiza modal full-screen quando user é admin de domínio que ainda não
 * tem aceite vigente do DPA. Sem aceitar, user só pode navegar /legal/* e
 * /help/* mas não consegue criar workspace / conectar fonte.
 *
 * Demais users do domínio (não-admins) vêem banner informativo via
 * DpaInfoBanner — não bloqueante.
 */
'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { ShieldCheck, X, AlertCircle } from 'lucide-react';
import { DPA_TITLE, DPA_CURRENT_VERSION, DPA_SUBPROCESSORS } from '@/lib/legal/dpa-version';

interface DpaStatus {
  needsAcceptance: boolean;
  currentVersion: string;
  orgKey?: string;
  canAccept?: boolean;
  acceptedBy?: {
    email: string;
    cargo: string | null;
    acceptedAt: string;
    roleClaim: string;
  };
  scope?: string;
}

type RoleClaim = 'legal_rep' | 'delegated' | 'self_claim';

export default function DpaGateModal() {
  const [status, setStatus] = useState<DpaStatus | null>(null);
  const [open, setOpen] = useState(false);
  const [cargo, setCargo] = useState('');
  const [roleClaim, setRoleClaim] = useState<RoleClaim>('legal_rep');
  const [ackLegalRep, setAckLegalRep] = useState(false);
  const [ackRead, setAckRead] = useState(false);
  const [ackController, setAckController] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'summary' | 'sign'>('summary');

  useEffect(() => {
    void fetch('/api/legal/dpa/status')
      .then((r) => r.json())
      .then((data: DpaStatus) => {
        setStatus(data);
        if (data.needsAcceptance && data.canAccept) setOpen(true);
      })
      .catch(() => {});
  }, []);

  async function submit() {
    if (!status?.orgKey || !ackRead || !ackController) {
      setError('Marque todos os acknowledgments obrigatórios');
      return;
    }
    if (roleClaim === 'legal_rep' && !ackLegalRep) {
      setError('Confirme que é representante legal');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/legal/dpa/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orgKey: status.orgKey,
          cargo: cargo.trim() || null,
          roleClaim,
          ackLegalRep,
          ackRead,
          ackController,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.error ?? 'Erro ao registrar aceite');
        setSubmitting(false);
        return;
      }
      setOpen(false);
      // refresh pra liberar todas as rotas gateadas
      window.location.reload();
    } catch (err: any) {
      setError(err.message ?? 'Erro de rede');
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="dpa-gate-title"
      className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm px-4 py-8"
    >
      <div className="w-full max-w-3xl rounded-xl border border-border bg-background shadow-2xl my-auto">
        <header className="flex items-start justify-between px-6 py-4 border-b border-border">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-6 w-6 text-blue-600 mt-0.5" />
            <div>
              <h2 id="dpa-gate-title" className="text-lg font-semibold">
                {DPA_TITLE}
              </h2>
              <p className="text-xs text-muted-foreground">
                Versão {DPA_CURRENT_VERSION} · Domínio <strong>{status?.orgKey}</strong>
              </p>
            </div>
          </div>
        </header>

        {step === 'summary' && (
          <div className="px-6 py-5 space-y-4">
            <p className="text-sm text-foreground">
              Antes de continuar, precisamos do aceite do <strong>{DPA_TITLE}</strong> em
              nome da sua empresa.
            </p>
            <p className="text-xs text-muted-foreground">
              Você está aceitando como <strong>Controlador</strong> dos dados. A Seraik atua
              como <strong>Operador</strong>, processando dados estritamente para os fins
              definidos no contrato comercial.
            </p>

            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <h3 className="text-sm font-medium mb-2">Resumo do que você está aceitando</h3>
              <ul className="space-y-1.5 text-xs text-muted-foreground list-disc pl-5">
                <li>Seraik trata dados de mídia (Google Ads, Meta, TikTok), analytics (GA4, Adobe) e do CRM da empresa</li>
                <li>Dados ficam armazenados em servidores conforme listagem de sub-processadores abaixo</li>
                <li>Seraik garante medidas técnicas: criptografia at-rest, RLS multi-tenant, audit log imutável</li>
                <li>Notificação de incidente de segurança em até 24h</li>
                <li>Direito de auditar medidas de segurança a qualquer momento</li>
                <li>Retenção: dados processados ficam disponíveis enquanto o contrato vigora + 30 dias pós-rescisão</li>
                <li>Direito de portabilidade: você pode exportar todos os dados (JSON/ZIP) e solicitar exclusão (LGPD art. 18)</li>
              </ul>
            </div>

            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <h3 className="text-sm font-medium mb-2">Sub-processadores</h3>
              <table className="w-full text-xs">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="text-left py-1">Empresa</th>
                    <th className="text-left py-1">Finalidade</th>
                    <th className="text-left py-1">Região</th>
                  </tr>
                </thead>
                <tbody>
                  {DPA_SUBPROCESSORS.map((s) => (
                    <tr key={s.name} className="border-t border-border/40">
                      <td className="py-1.5">{s.name}</td>
                      <td className="py-1.5 text-muted-foreground">{s.purpose}</td>
                      <td className="py-1.5 text-muted-foreground">{s.region}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-muted-foreground">
              <Link href="/legal/dpa" target="_blank" className="underline hover:text-foreground">
                Ler documento completo →
              </Link>
            </p>

            <div className="flex justify-between gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  void fetch('/api/auth/signout', { method: 'POST' }).finally(() => {
                    window.location.href = '/login';
                  });
                }}
                className="px-3 py-1.5 text-xs font-medium rounded-md border border-border hover:bg-muted"
              >
                Sair sem aceitar
              </button>
              <button
                type="button"
                onClick={() => setStep('sign')}
                className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
              >
                Avançar para o aceite
              </button>
            </div>
          </div>
        )}

        {step === 'sign' && (
          <div className="px-6 py-5 space-y-4">
            <div>
              <label className="block text-xs font-medium mb-1">Seu cargo</label>
              <input
                type="text"
                value={cargo}
                onChange={(e) => setCargo(e.target.value)}
                placeholder="Ex: CMO, Diretor de Marketing, CEO..."
                className="w-full px-3 py-2 text-sm rounded-md border border-border bg-background"
              />
            </div>

            <fieldset className="space-y-2">
              <legend className="text-xs font-medium mb-1">Em qual condição você está aceitando?</legend>
              <label className="flex items-start gap-2 p-3 rounded-md border border-border cursor-pointer hover:border-blue-500/40">
                <input
                  type="radio"
                  name="roleClaim"
                  checked={roleClaim === 'legal_rep'}
                  onChange={() => setRoleClaim('legal_rep')}
                  className="mt-0.5"
                />
                <div>
                  <div className="text-sm font-medium">Sou representante legal autorizado</div>
                  <div className="text-xs text-muted-foreground">
                    Tenho poderes pra assinar contratos em nome da empresa. Recomendado pra C-level / sócios.
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-2 p-3 rounded-md border border-border cursor-pointer hover:border-blue-500/40">
                <input
                  type="radio"
                  name="roleClaim"
                  checked={roleClaim === 'delegated'}
                  onChange={() => setRoleClaim('delegated')}
                  className="mt-0.5"
                />
                <div>
                  <div className="text-sm font-medium">Tenho delegação interna pra aceitar</div>
                  <div className="text-xs text-muted-foreground">
                    Recebi autorização do jurídico/diretoria. Aceite válido até o representante legal ratificar.
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-2 p-3 rounded-md border border-border cursor-pointer hover:border-blue-500/40">
                <input
                  type="radio"
                  name="roleClaim"
                  checked={roleClaim === 'self_claim'}
                  onChange={() => setRoleClaim('self_claim')}
                  className="mt-0.5"
                />
                <div>
                  <div className="text-sm font-medium">Aceito sob minha responsabilidade individual</div>
                  <div className="text-xs text-muted-foreground">
                    Última opção. Audit log registrará isso. Recomendamos delegar ao seu jurídico antes.
                  </div>
                </div>
              </label>
            </fieldset>

            <div className="space-y-2 pt-2 border-t border-border">
              {roleClaim === 'legal_rep' && (
                <label className="flex items-start gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={ackLegalRep}
                    onChange={(e) => setAckLegalRep(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>Confirmo que sou representante legal autorizado a celebrar este DPA.</span>
                </label>
              )}
              <label className="flex items-start gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={ackRead}
                  onChange={(e) => setAckRead(e.target.checked)}
                  className="mt-0.5"
                />
                <span>Li (ou tive oportunidade de ler) o documento completo do DPA versão {DPA_CURRENT_VERSION}.</span>
              </label>
              <label className="flex items-start gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={ackController}
                  onChange={(e) => setAckController(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  Estou ciente de que minha empresa será o <strong>Controlador</strong> dos dados pessoais
                  e a Seraik atuará como <strong>Operador</strong>.
                </span>
              </label>
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-md border border-red-500/40 bg-red-500/10 text-xs text-red-700 dark:text-red-300">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex justify-between gap-2 pt-2">
              <button
                type="button"
                onClick={() => setStep('summary')}
                disabled={submitting}
                className="px-3 py-1.5 text-xs font-medium rounded-md border border-border hover:bg-muted"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Registrando…' : 'Aceitar e continuar'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Banner informativo pra não-admins do domínio.
 * Aparece em todas as páginas mostrando "DPA aceito por X em Y".
 */
export function DpaInfoBanner() {
  const [status, setStatus] = useState<DpaStatus | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    void fetch('/api/legal/dpa/status')
      .then((r) => r.json())
      .then((data: DpaStatus) => setStatus(data))
      .catch(() => {});
    if (typeof window !== 'undefined' && window.localStorage.getItem('dpa_banner_dismissed') === '1') {
      setDismissed(true);
    }
  }, []);

  if (!status || dismissed) return null;
  if (status.needsAcceptance) return null; // gate modal cuida
  if (!status.acceptedBy) return null;

  return (
    <div className="border-b border-border bg-muted/30 px-4 py-2 text-xs flex items-center justify-between gap-4">
      <div className="flex items-center gap-2 text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
        <span>
          DPA aceito por <strong>{status.acceptedBy.email}</strong>
          {status.acceptedBy.cargo ? ` (${status.acceptedBy.cargo})` : ''} em{' '}
          {new Date(status.acceptedBy.acceptedAt).toLocaleDateString('pt-BR')}.{' '}
          <Link href="/legal/dpa" className="underline hover:text-foreground">
            Ver detalhes
          </Link>
        </span>
      </div>
      <button
        type="button"
        onClick={() => {
          window.localStorage.setItem('dpa_banner_dismissed', '1');
          setDismissed(true);
        }}
        className="text-muted-foreground hover:text-foreground"
        aria-label="Fechar"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
